import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import LineChart from '../../../components/charts/LineChart';
import supabase from '../../../supabase/supabase';

export default function BarberEarnings() {
  const [stats, setStats] = useState({ weekly: 0, withdrawable: 0, lifetime: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch all completed bookings (cash collected on completion)
        const { data: bookings } = await supabase
          .from('bookings_1740423033529')
          .select('*, customer:customer_id(full_name)')
          .eq('barber_id', user.id)
          .eq('status', 'completed')
          .order('booking_date', { ascending: false });

        if (bookings) {
          const total = bookings.reduce((sum, b) => sum + Number(b.total_price), 0);

          // Calculate weekly earnings (last 7 days)
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          const weeklyTotal = bookings
            .filter(b => new Date(b.booking_date) >= oneWeekAgo)
            .reduce((sum, b) => sum + Number(b.total_price), 0);

          setStats({
            weekly: weeklyTotal,
            withdrawable: total,
            lifetime: total
          });
          setTransactions(bookings);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, []);

  return (
    <div className="animate-in fade-in duration-500">
      <h1 className="text-3xl font-bold text-brand-black mb-8">Earnings</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <p className="text-sm text-gray-500 font-medium mb-1">Weekly Revenue</p>
          <p className="text-3xl font-bold text-brand-black">Rs. {stats.weekly.toLocaleString()}</p>
          <div className="mt-2 flex items-center text-gray-400 text-sm font-medium">
            <SafeIcon name="TrendingUp" className="mr-1" />
            Last 7 days
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <p className="text-sm text-gray-500 font-medium mb-1">Available for Payout</p>
          <p className="text-3xl font-bold text-brand-black">Rs. {stats.withdrawable.toLocaleString()}</p>
          <button className="mt-2 text-brand-primary font-bold text-sm hover:underline">Withdraw Funds</button>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <p className="text-sm text-gray-500 font-medium mb-1">Life-time Earnings</p>
          <p className="text-3xl font-bold text-brand-black">Rs. {stats.lifetime.toLocaleString()}</p>
          <p className="mt-2 text-gray-400 text-sm italic">All Completed Services</p>
        </div>
      </div>

      <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm mb-8">
        <h2 className="text-xl font-bold mb-6">Revenue Trend (LKR)</h2>
        <LineChart
          data={stats.lifetime === 0 ? [0, 0, 0, 0, 0, 0, 0] : [0, 0, 0, 0, 0, 0, stats.lifetime]}
          title="Revenue"
          color="#0047FF"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold">Recent Completed Transactions</h2>
        </div>
        <div className="overflow-x-auto">
          {transactions.length > 0 ? (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold">Date</th>
                  <th className="px-6 py-4 font-bold">Client</th>
                  <th className="px-6 py-4 font-bold">Amount</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-600">{tx.booking_date}</td>
                    <td className="px-6 py-4 font-bold text-brand-black">{tx.customer?.full_name || 'Unknown'}</td>
                    <td className="px-6 py-4 font-bold text-brand-black">Rs. {Number(tx.total_price).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded uppercase">
                        Paid
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-gray-400 italic">
              No completed transactions yet. Complete your first booking to see earnings!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}